/**
 * Unlimited-credit helpers.
 *
 * A store can be granted unlimited credits per type (`Restaurant.unlimitedCredits`:
 * `'ai' | 'voip' | 'mail' | 'sms'`), and the credit status endpoints echo an additive
 * `unlimited` flag. For unlimited types store users must not see a balance, a low-credit
 * warning or a top-up prompt; platform staff see a "Korlátlan" badge instead.
 */
import {collectUserRoles} from './managerRoles';

/** Credit types that can be unlimited. */
export const CREDIT_TYPES = ['ai', 'voip', 'mail', 'sms'];

/**
 * Platform staff = `system:admin` or any `sales:*` role.
 * @param {object} [user] The signed-in user (as returned by `/me`).
 * @returns {boolean}
 */
export const isPlatformStaff = (user) => collectUserRoles(user, null)
    .some((role) => role === 'system:admin' || String(role).startsWith('sales:'));

/**
 * Pick the restaurant doc out of `user.restaurants` (by id, else the first one).
 * @param {object} [user] The signed-in user.
 * @param {string} [restaurantId] Preferred restaurant id.
 * @returns {object|null}
 */
export const pickRestaurant = (user, restaurantId) => {
    const list = Array.isArray(user?.restaurants) ? user.restaurants.filter((r) => r && typeof r === 'object') : [];
    if (restaurantId) {
        const hit = list.find((r) => String(r._id || r.id) === String(restaurantId));
        if (hit) {
            return hit;
        }
    }

    return list[0] || null;
};

/**
 * Is the credit type unlimited for this store?
 * @param {{restaurant?: object, type?: string, status?: object}} params `status` is a credit status response.
 * @returns {boolean}
 */
export const isCreditUnlimited = ({restaurant, type, status} = {}) => status?.unlimited === true
    || (Array.isArray(restaurant?.unlimitedCredits) && restaurant.unlimitedCredits.includes(type));

/**
 * Should balance / low-credit / top-up UI be hidden from the current user?
 * (unlimited AND not platform staff)
 * @param {{user?: object, restaurant?: object, type?: string, status?: object}} params
 * @returns {boolean}
 */
export const shouldHideCredit = ({user, restaurant, type, status} = {}) => isCreditUnlimited({restaurant, type, status})
    && !isPlatformStaff(user);
